'use client';

import type { ReactNode } from 'react';
import useSWR from 'swr';
import { getAbTestingData } from '@/api/public';
import { getWapHomePageSupplementData, getWapHomePageInitData } from '@/api/home';
import { useStore } from '@/providers/app-store-provider';
import { getStoreInstance } from '@/store';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import StreamingHomePage from './StreamingHomePage';
import { US_WAP_COMPONENT_MAP } from './component-map.us-csr';
import { US_2026_WAP_COMPONENT_MAP } from './versions/2026-wap/component-map.us-csr';
import {
  normalizeBlockItems,
  normalize2026WapBlockInitData,
  supplement2026WapBrandImageBackground,
  type Wap2026BlockItem,
} from './versions/2026-wap/normalize-block-items';

// 只限制首页自身的等待，不改变公共请求封装的行为。
async function withTimeout<T>(request: Promise<T>, timeoutMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      request,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('WAP homepage request timed out')), timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

async function fetchWapHomeData([, token, currency]: readonly [
  string,
  string,
  string | undefined,
]) {
  const assertCurrentIdentity = () => {
    const state = getStoreInstance()?.getState();
    if (
      !state ||
      state.token !== token ||
      (state.currencyReady ? state.currency?.name : undefined) !== currency
    ) {
      throw new Error('WAP homepage request identity changed');
    }
  };
  assertCurrentIdentity();
  let blockType = 202;
  try {
    const response = await withTimeout(
      getAbTestingData({ param_name: 'is_wap_homepage_v2026_v2' }),
      1500,
    );
    if (response?.code !== 200) throw new Error('Failed to get WAP homepage AB group');
    const value = response.data?.strategy_variable;
    if (value === 2 || value === '2') blockType = 204;
  } catch (error) {
    logger.error('Failed to get 2026 WAP homepage AB group:', error);
  }

  // 登录切换后不再继续旧请求，避免把新身份配置写入旧身份缓存。
  assertCurrentIdentity();
  const [initResponse, completeResponse] = await withTimeout(
    Promise.all([
      getWapHomePageInitData({ block_type: blockType, status: 1 }),
      blockType === 204
        ? withTimeout(getWapHomePageSupplementData(), 5000).catch((error: unknown) => {
            logger.error('Failed to supplement 2026 WAP homepage block fields:', error);
            return undefined;
          })
        : undefined,
    ]),
    10000,
  );
  assertCurrentIdentity();
  if (initResponse?.code !== 200) throw new Error('Failed to fetch WAP homepage blocks');
  const list = Array.isArray(initResponse.data)
    ? initResponse.data.filter(
        (item): item is Wap2026BlockItem =>
          typeof item === 'object' && item !== null && !Array.isArray(item),
      )
    : [];
  if (blockType !== 204) return { blockType, listData: normalizeBlockItems(list) };

  const completeList: Wap2026BlockItem[] =
    completeResponse?.code === 200 && Array.isArray(completeResponse.data?.list)
      ? completeResponse.data.list
      : [];
  const supplementedList = supplement2026WapBrandImageBackground(list, completeList).map(
    (block) =>
      completeList.find(
        (item) =>
          block.component_id === 'swipeImage2026' &&
          item.component_id === block.component_id &&
          item.blocks_advances_id === block.blocks_advances_id,
      ) ?? block,
  );
  return { blockType, listData: normalize2026WapBlockInitData(supplementedList) };
}

export default function WapHomePageClient({
  children,
  deviceType,
}: {
  children: ReactNode;
  deviceType: EPlatform;
}) {
  const token = useStore((state) => state.token);
  const ready = useStore((state) => state.clientTokenReady && state.currencyReady);
  const currency = useStore((state) => (state.currencyReady ? state.currency?.name : undefined));
  const { data, error } = useSWR(
    ready ? ['wap-homepage', token, currency] : null,
    fetchWapHomeData,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      keepPreviousData: false,
      onError: (requestError: unknown) =>
        logger.error('Failed to fetch WAP homepage:', requestError),
    },
  );

  if (!ready || (!data && !error)) return children;
  return (
    <StreamingHomePage
      homeListData={data?.listData ?? []}
      deviceType={deviceType}
      componentMap={data?.blockType === 204 ? US_2026_WAP_COMPONENT_MAP : US_WAP_COMPONENT_MAP}
    />
  );
}
